import type { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  AddToCollectionLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { tagged } from "@mirage/service-logging";
import { createNewSection } from "@mirage/service-stacks/service/utils";
import i18n from "@mirage/translations";
import { useAtom } from "jotai";
import { activeStackSectionsAtom } from "../atoms";
import { useCreateStackSection } from "../hooks/FullScreenStackHooks";
import { useSectionsNavigation } from "../hooks/sectionHooks";
import { canEditStack } from "../utils";
import styles from "./StackFloatButton.module.css";

type AddMenuOption = "section";

const logger = tagged("StackFloatButton");

export const StackFloatButton = ({ stack }: { stack: stacks.Stack | null }) => {
  const createStackSection = useCreateStackSection();
  const { setNewestSectionId } = useSectionsNavigation();
  const [{ allSections }] = useAtom(activeStackSectionsAtom);
  const hasEditPermissions = canEditStack(stack);

  const handleAddSection = async () => {
    if (!stack?.namespace_id || !allSections) return;

    // Create new section with default name
    const newSection = createNewSection(i18n.t("new_section_name"));
    setNewestSectionId(newSection.id);
    const newSectionId = await createStackSection(newSection);
    if (!newSectionId) {
      setNewestSectionId(null);
      return;
    }
  };

  const handleSelection = (value: AddMenuOption) => {
    switch (value) {
      case "section":
        handleAddSection();
        break;
      default:
        logger.warn("Unhandled menu selection:", value);
    }
  };

  if (!hasEditPermissions) {
    return null;
  }

  return (
    <div className={styles.stackFloatButton}>
      <Menu.Wrapper onSelection={handleSelection}>
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              variant="primary"
              withIconStart={<UIIcon src={AddLine} />}
              {...getTriggerProps()}
            >
              {i18n.t("add")}
            </Button>
            <Menu.Content
              {...getContentProps()}
              placement="top-start"
              triggerOffset={8}
            >
              <Menu.Segment>
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={AddToCollectionLine} />}
                  value={"section" as AddMenuOption}
                >
                  {i18n.t("section")}
                </Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    </div>
  );
};
